import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CarService } from 'src/app/services/car/car.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import { InventoryCar } from 'src/app/shared/models/car.interface';
import Swal from 'sweetalert2';

interface FilterOption {
  value: string;
  label: string;
  checked?: boolean;
}

interface LiveAuctionFilters {
  dates: string[];
  auctions: string[];
  makes: string[];
  models: string[];
}

interface CachedMakeData {
  name: string;
  slug: string;
  count: number;
  models: { [modelSlug: string]: CachedModelData };
}

interface CachedModelData {
  name: string;
  slug: string;
  chassis_model: string[];
}

interface MakesDateStructure {
  [date: string]: {
    [makeSlug: string]: CachedMakeData;
  };
}

@Component({
  selector: 'app-vh-live-auction',
  templateUrl: './vh-live-auction.component.html',
  styleUrls: ['./vh-live-auction.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VhLiveAuctionComponent extends DestructorComponent implements OnInit, OnDestroy {

  // Data properties
  vehicles: InventoryCar[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  isLoadingCachedData: boolean = false;
  isLoadingMakes: boolean = false;
  isLoadingModels: boolean = false;
  currentPage: number = 1;

  // Filter data
  dateOptions: FilterOption[] = [];
  auctionOptions: FilterOption[] = [];
  makeOptions: FilterOption[] = [];
  modelOptions: FilterOption[] = [];

  // Cached data for smart filtering
  makesDateData: MakesDateStructure = {};
  dateHalls: { [key: string]: string[] } = {};

  // Selected filters
  filters: LiveAuctionFilters = {
    dates: [],
    auctions: [],
    makes: [],
    models: []
  };

  // UI state
  showFiltersForm: boolean = true;

  // Expose Math to template
  Math = Math;

  constructor(
    private _carService: CarService,
    private _generalService: GeneralService,
    private _vehicleService: VehicleService
  ) {
    super();
  }

  ngOnInit(): void {
    this.generateDateOptions();
    this.loadCachedMakeData();
    this.loadVehicles();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  // Generate date options for next 7 days
  generateDateOptions(): void {
    const today = new Date();
    this.dateOptions = [];
    this.filters.dates = []; // Initialize dates filter array

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dateString = date.toISOString().split('T')[0];
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });

      this.dateOptions.push({
        value: dateString,
        label: label,
        checked: true // All dates checked by default
      });

      // Add to filters array since all are checked by default
      this.filters.dates.push(dateString);
    }
    console.log('Date options generated with all checked:', this.filters.dates.length);
  }

  // Populate auction options from date_halls data
  private populateAuctionOptions(): void {
    if (!this.dateHalls || Object.keys(this.dateHalls).length === 0) {
      console.log('No date_halls data available');
      this.auctionOptions = [];
      return;
    }

    // Get all unique halls from date_halls
    const allHalls = new Set<string>();
    Object.keys(this.dateHalls).forEach(date => {
      if (Array.isArray(this.dateHalls[date])) {
        this.dateHalls[date].forEach(hall => allHalls.add(hall));
      }
    });

    // Create auction options
    this.auctionOptions = Array.from(allHalls).map(hall => ({
      value: hall,
      label: hall,
      checked: false
    }));

    // Sort alphabetically
    this.auctionOptions.sort((a, b) => a.label.localeCompare(b.label));

    console.log('Auction options populated:', this.auctionOptions.length);

    // Update halls based on initially selected dates (all dates are checked by default)
    this.updateHallsBasedOnDates();
  }

  // Load cached make data from API (called once)
  private loadCachedMakeData(): void {
    this.isLoadingCachedData = true;
    this.isLoadingMakes = true;

    this.subscriptions.push(
      this._generalService.GetCachedData('alaedo-data').subscribe({
        next: (response: any) => {
          if (response.status && response.data && response.data.make_dates) {
            this.makesDateData = response.data.make_dates;
            console.log('Cached make data loaded:', Object.keys(this.makesDateData).length, 'dates');
            this.populateMakeOptions();
          }
          // Extract date_halls if present
          if (response.status && response.data && response.data.date_halls) {
            this.dateHalls = response.data.date_halls;
            console.log('Date halls loaded:', this.dateHalls);
            // Auto-check halls based on initially selected dates (all dates are checked by default)
            this.populateAuctionOptions();
          }
          this.isLoadingCachedData = false;
          this.isLoadingMakes = false;
        },
        error: (error: any) => {
          console.error('Error loading cached make data:', error);
          this.isLoadingCachedData = false;
          this.isLoadingMakes = false;
        }
      })
    );
  }

  // Populate make options from cached data based on selected dates
  private populateMakeOptions(): void {
    const selectedDates = this.filters.dates;
    if (selectedDates.length === 0) {
      this.makeOptions = [];
      return;
    }

    // Aggregate makes across selected dates
    const makeMap: { [makeSlug: string]: { name: string; count: number } } = {};

    selectedDates.forEach(date => {
      if (this.makesDateData[date]) {
        Object.keys(this.makesDateData[date]).forEach(makeSlug => {
          const make = this.makesDateData[date][makeSlug];
          if (makeMap[makeSlug]) {
            // Make already exists, add to count
            makeMap[makeSlug].count += make.count;
          } else {
            // New make, initialize
            makeMap[makeSlug] = {
              name: make.name,
              count: make.count
            };
          }
        });
      }
    });

    // Create filter options
    this.makeOptions = Object.keys(makeMap).map(makeSlug => ({
      value: makeSlug,
      label: `${makeMap[makeSlug].name} (${makeMap[makeSlug].count})`,
      checked: false
    }));

    // Sort makes alphabetically by name
    this.makeOptions.sort((a, b) => {
      const nameA = makeMap[a.value].name.toLowerCase();
      const nameB = makeMap[b.value].name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    console.log('Make options populated:', this.makeOptions.length, 'for dates:', selectedDates);
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).toUpperCase();
  }

  // Load vehicles data
  loadVehicles(page: number = 1): void {
    this.isLoading = true;
    this.currentPage = page;

    // Build filters for API call
    const apiFilters: Record<string, any> = {};

    if (this.filters.dates.length > 0) {
      apiFilters['auction_dates'] = this.filters.dates.join(',');
    }

    if (this.filters.auctions.length > 0) {
      apiFilters['auctions'] = this.filters.auctions.join(',');
    }

    if (this.filters.makes.length > 0) {
      apiFilters['makes'] = this.filters.makes.join(',');
    }

    if (this.filters.models.length > 0) {
      apiFilters['models'] = this.filters.models.join(',');
    }

    apiFilters.type = 'alaedo';
    apiFilters.is_added_to_stock = "false";

    this.subscriptions.push(
      this._carService.GetDataPost(page.toString(), '', apiFilters).subscribe({
        next: (response: PaginatedResponse<InventoryCar>) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.vehicles = response.data.data || [];
            this.paginationInfo = response.data.pagination;
          } else {
            this.vehicles = [];
            this.resetPagination();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading vehicles:', error);
          this.vehicles = [];
          this.resetPagination();
        }
      })
    );
  }

  // Reset pagination
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Handle page change
  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.paginationInfo.lastPage) {
      this.loadVehicles(page);
    }
  }

  // Update auction halls based on selected dates
  private updateHallsBasedOnDates(): void {
    if (!this.dateHalls || Object.keys(this.dateHalls).length === 0) {
      console.log('No date_halls data available');
      return;
    }

    // Get all halls that should be checked based on selected dates
    const hallsToCheck = new Set<string>();

    this.filters.dates.forEach(selectedDate => {
      if (this.dateHalls[selectedDate] && Array.isArray(this.dateHalls[selectedDate])) {
        this.dateHalls[selectedDate].forEach(hall => {
          hallsToCheck.add(hall);
        });
      }
    });

    console.log('Halls to check based on selected dates:', Array.from(hallsToCheck));

    // Update auction options - check halls that are in the selected dates, uncheck others
    this.auctionOptions.forEach(option => {
      const shouldBeChecked = hallsToCheck.has(option.value);
      option.checked = shouldBeChecked;

      // Update filters array
      const indexInFilters = this.filters.auctions.indexOf(option.value);
      if (shouldBeChecked && indexInFilters === -1) {
        // Should be checked but not in filters - add it
        this.filters.auctions.push(option.value);
      } else if (!shouldBeChecked && indexInFilters > -1) {
        // Should not be checked but is in filters - remove it
        this.filters.auctions.splice(indexInFilters, 1);
      }
    });

    // Sort auction options: checked ones on top, unchecked ones at bottom
    this.auctionOptions.sort((a, b) => {
      // If both are checked or both are unchecked, maintain alphabetical order
      if (a.checked === b.checked) {
        return a.label.localeCompare(b.label);
      }
      // Checked items come first (return -1 if a is checked and b is not)
      return a.checked ? -1 : 1;
    });

    console.log('Updated auction filters:', this.filters.auctions);
  }

  // Check if all auctions are checked
  isAllAuctionsChecked(): boolean {
    if (this.auctionOptions.length === 0) {
      return false;
    }
    return this.auctionOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for auctions
  onAuctionAllChange(checked: boolean): void {
    // Update all auction options
    this.auctionOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all auction values to filters
      this.filters.auctions = this.auctionOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.auctions = [];
    }

    console.log('All auctions', checked ? 'checked' : 'unchecked', ':', this.filters.auctions);
  }

  // Check if all makes are checked
  isAllMakesChecked(): boolean {
    if (this.makeOptions.length === 0) {
      return false;
    }
    return this.makeOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for makes
  onMakeAllChange(checked: boolean): void {
    // Update all make options
    this.makeOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all make values to filters
      this.filters.makes = this.makeOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.makes = [];
    }

    // Clear dependent filters (models)
    this.filters.models = [];
    this.modelOptions.forEach(option => option.checked = false);

    // Update model options based on selected makes and dates
    if (checked) {
      this.updateModelOptions();
    } else {
      this.modelOptions = [];
    }

    console.log('All makes', checked ? 'checked' : 'unchecked', ':', this.filters.makes);
  }

  // Check if all models are checked
  isAllModelsChecked(): boolean {
    if (this.modelOptions.length === 0) {
      return false;
    }
    return this.modelOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for models
  onModelAllChange(checked: boolean): void {
    // Update all model options
    this.modelOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all model values to filters
      this.filters.models = this.modelOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.models = [];
    }

    console.log('All models', checked ? 'checked' : 'unchecked', ':', this.filters.models);
  }

  // Check if all dates are checked
  isAllDatesChecked(): boolean {
    if (this.dateOptions.length === 0) {
      return false;
    }
    return this.dateOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for dates
  onDateAllChange(checked: boolean): void {
    // Update all date options
    this.dateOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all date values to filters
      this.filters.dates = this.dateOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.dates = [];
    }

    // Update halls based on date changes
    this.updateHallsBasedOnDates();

    // Refresh makes based on new dates
    this.populateMakeOptions();

    // Clear dependent filters (makes and models)
    this.filters.makes = [];
    this.filters.models = [];
    this.makeOptions.forEach(option => option.checked = false);
    this.modelOptions = [];

    console.log('All dates', checked ? 'checked' : 'unchecked', ':', this.filters.dates);
  }

  // Filter methods
  onDateFilterChange(dateValue: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const checked = target.checked;

    // Update the option's checked state
    const dateOption = this.dateOptions.find(option => option.value === dateValue);
    if (dateOption) {
      dateOption.checked = checked;
    }

    if (checked) {
      if (!this.filters.dates.includes(dateValue)) {
        this.filters.dates.push(dateValue);
      }
    } else {
      this.filters.dates = this.filters.dates.filter(d => d !== dateValue);
    }

    // Update halls based on date changes
    this.updateHallsBasedOnDates();

    // Refresh makes based on new dates
    this.populateMakeOptions();

    // Clear dependent filters (makes and models)
    this.filters.makes = [];
    this.filters.models = [];
    this.makeOptions.forEach(option => option.checked = false);
    this.modelOptions = [];

    console.log('Date filters updated:', this.filters.dates);
  }

  onAuctionFilterChange(auctionValue: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const checked = target.checked;

    // Update the option's checked state
    const auctionOption = this.auctionOptions.find(option => option.value === auctionValue);
    if (auctionOption) {
      auctionOption.checked = checked;
    }

    if (checked) {
      if (!this.filters.auctions.includes(auctionValue)) {
        this.filters.auctions.push(auctionValue);
      }
    } else {
      this.filters.auctions = this.filters.auctions.filter(a => a !== auctionValue);
    }
    console.log('Auction filters updated:', this.filters.auctions);
  }

  onMakeFilterChange(makeSlug: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const checked = target.checked;

    // Update the option's checked state
    const makeOption = this.makeOptions.find(option => option.value === makeSlug);
    if (makeOption) {
      makeOption.checked = checked;
    }

    if (checked) {
      if (!this.filters.makes.includes(makeSlug)) {
        this.filters.makes.push(makeSlug);
      }
    } else {
      this.filters.makes = this.filters.makes.filter(m => m !== makeSlug);
      // Clear related models when make is unchecked
      this.clearModelsForMake(makeSlug);
    }

    // Update model options based on selected makes
    this.updateModelOptions();
    console.log('Make filters updated:', this.filters.makes);
  }

  onModelFilterChange(modelSlug: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const checked = target.checked;

    // Update the option's checked state
    const modelOption = this.modelOptions.find(option => option.value === modelSlug);
    if (modelOption) {
      modelOption.checked = checked;
    }

    if (checked) {
      if (!this.filters.models.includes(modelSlug)) {
        this.filters.models.push(modelSlug);
      }
    } else {
      this.filters.models = this.filters.models.filter(m => m !== modelSlug);
    }
    console.log('Model filters updated:', this.filters.models);
  }

  // Clear models for a specific make
  private clearModelsForMake(makeSlug: string): void {
    const selectedDates = this.filters.dates;
    const modelSlugsToRemove = new Set<string>();

    // Collect all model slugs for this make across selected dates
    selectedDates.forEach(date => {
      if (this.makesDateData[date] && this.makesDateData[date][makeSlug]) {
        const make = this.makesDateData[date][makeSlug];
        if (make.models) {
          Object.keys(make.models).forEach(modelSlug => {
            modelSlugsToRemove.add(modelSlug);
          });
        }
      }
    });

    // Remove these models from filters
    this.filters.models = this.filters.models.filter(modelSlug =>
      !modelSlugsToRemove.has(modelSlug)
    );
  }

  // Update model options based on selected makes and dates
  private updateModelOptions(): void {
    const selectedMakes = this.filters.makes;
    const selectedDates = this.filters.dates;

    if (selectedMakes.length === 0 || selectedDates.length === 0) {
      this.modelOptions = [];
      return;
    }

    this.isLoadingModels = true;

    // Aggregate models across selected dates and makes
    const modelMap: { [modelSlug: string]: { name: string } } = {};

    selectedDates.forEach(date => {
      if (this.makesDateData[date]) {
        selectedMakes.forEach(makeSlug => {
          const make = this.makesDateData[date][makeSlug];
          if (make && make.models) {
            Object.keys(make.models).forEach(modelSlug => {
              const model = make.models[modelSlug];
              if (!modelMap[modelSlug]) {
                modelMap[modelSlug] = {
                  name: model.name
                };
              }
            });
          }
        });
      }
    });

    // Create filter options
    this.modelOptions = Object.keys(modelMap).map(modelSlug => ({
      value: modelSlug,
      label: modelMap[modelSlug].name,
      checked: this.filters.models.includes(modelSlug)
    }));

    // Sort models alphabetically by name
    this.modelOptions.sort((a, b) => a.label.localeCompare(b.label));

    this.isLoadingModels = false;
    console.log('Model options updated:', this.modelOptions.length, 'for dates:', selectedDates, 'and makes:', selectedMakes);
  }

  // Apply filters
  applyFilters(): void {
    console.log('Applying filters:', this.filters);
    this.loadVehicles(1);
  }

  // Clear all filters
  clearFilters(): void {
    // Reset date filters to all checked
    this.filters.dates = this.dateOptions.map(option => {
      option.checked = true;
      return option.value;
    });

    // Reset auction filters - will be auto-updated based on dates
    this.filters.auctions = [];
    this.auctionOptions.forEach(option => option.checked = false);
    this.updateHallsBasedOnDates();

    // Reset make filters
    this.filters.makes = [];
    this.makeOptions.forEach(option => option.checked = false);

    // Reset model filters
    this.filters.models = [];
    this.modelOptions = [];

    console.log('Filters cleared');
    this.loadVehicles(1);
  }

  // Toggle filters visibility
  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  // Vehicle actions
  onSaveVehicle(vehicle: InventoryCar): void {
    console.log('Save vehicle:', vehicle);
    Swal.fire({
      title: 'Save Vehicle',
      text: `Do you want to save ${vehicle.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        // Implement save logic here
        this.performAddToStock(vehicle);
      }
    });
  }

  private performAddToStock(vehicle: any): void {
    const vehicleId = vehicle._id;

    // Add to loading set
    // this.addingToStockIds.add(vehicleId);

    this.subscriptions.push(
      this._vehicleService.AddToStock(vehicleId).subscribe({
        next: (response: any) => {
          // Remove from loading set
          // this.addingToStockIds.delete(vehicleId);

          if (response.status) {
            // Show success message
            Swal.fire({
              title: 'Success!',
              text: 'Vehicle has been successfully added to real stock.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });

            // Reload the listing to reflect changes
            this.loadVehicles(this.currentPage);
          } else {
            // Show error message
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add vehicle to real stock.',
              icon: 'error',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        },
        error: (error) => {
          // Remove from loading set
          // this.addingToStockIds.delete(vehicleId);

          console.error('Error adding vehicle to stock:', error);

          // Show error message
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding vehicle to real stock.',
            icon: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  onMakeBid(vehicle: InventoryCar): void {
    console.log('Make bid for vehicle:', vehicle);
    window.open('/bid-manager/search-bids/view-bid/' + vehicle._id, '_blank');
  }

  // Utility methods
  formatPrice(price: string | number): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    console.log({numPrice});
    if (isNaN(numPrice)) return '¥0';
    return '¥' + (numPrice?numPrice.toLocaleString():"0");
  }

  formatMileage(mileage: number): string {
    if (!mileage) return '0 km';
    return mileage.toLocaleString() + ' km';
  }

  trackByFn(index: number, item: InventoryCar): any {
    return item._id || item.doc_id || index;
  }
}
